/**
 * Character Memory — Commercial Pipeline 唯一 Memory Layer
 *
 * 非 LLM、非 DB：由 OperationalSnapshot（资产聚合）派生。
 */
import { calculateWeeklyBalance } from './memoryCalculator';
import type { CharacterMemory, CharacterMemoryPatch, WeeklyActivityCounts } from './memoryTypes';
import type { DecisionTone, OperationalSnapshot } from './types';
import { shanghaiDateKey } from './characterMemoryLite';

function clamp01(n: number): number {
  if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function nonNegInt(n: unknown): number {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v) || v < 0) return 0;
  return Math.floor(v);
}

export function createDefaultMemory(
  characterId: number,
  asOfDate = shanghaiDateKey(),
  today_index = 1,
): CharacterMemory {
  return {
    characterId,
    asOfDate,
    today_index: Math.max(1, Math.min(7, today_index)),
    last_post_date: null,
    last_photo_date: null,
    last_video_date: null,
    last_voice_date: null,
    last_talking_date: null,
    last_rest_day: null,
    commercial_ratio: 0,
    trust_ratio: 0,
    interaction_ratio: 0,
    energy_level: 1,
    fatigue_score: 0,
    photo_count_this_week: 0,
    video_count_this_week: 0,
    talking_count_this_week: 0,
    voice_count_this_week: 0,
    rest_count_this_week: 0,
    selfie_count_this_week: 0,
    yesterday_had_selfie: false,
    yesterday_had_talking: false,
    consecutive_commercial_days: 0,
  };
}

/** 规范化：比率夹到 0–1，计数非负，today_index 1–7 */
export function normalizeMemory(raw: CharacterMemoryPatch & Pick<CharacterMemory, 'characterId'>): CharacterMemory {
  const base = createDefaultMemory(raw.characterId, raw.asOfDate, raw.today_index);
  const merged = { ...base, ...raw };
  return {
    ...merged,
    today_index: Math.max(1, Math.min(7, nonNegInt(merged.today_index) || 1)),
    commercial_ratio: clamp01(merged.commercial_ratio),
    trust_ratio: clamp01(merged.trust_ratio),
    interaction_ratio: clamp01(merged.interaction_ratio),
    energy_level: clamp01(merged.energy_level),
    fatigue_score: clamp01(merged.fatigue_score),
    photo_count_this_week: nonNegInt(merged.photo_count_this_week),
    video_count_this_week: nonNegInt(merged.video_count_this_week),
    talking_count_this_week: nonNegInt(merged.talking_count_this_week),
    voice_count_this_week: nonNegInt(merged.voice_count_this_week),
    rest_count_this_week: nonNegInt(merged.rest_count_this_week),
    selfie_count_this_week: nonNegInt(merged.selfie_count_this_week),
    yesterday_had_selfie: !!merged.yesterday_had_selfie,
    yesterday_had_talking: !!merged.yesterday_had_talking,
    consecutive_commercial_days: nonNegInt(merged.consecutive_commercial_days),
    last_post_date: merged.last_post_date || null,
    last_photo_date: merged.last_photo_date || null,
    last_video_date: merged.last_video_date || null,
    last_voice_date: merged.last_voice_date || null,
    last_talking_date: merged.last_talking_date || null,
    last_rest_day: merged.last_rest_day || null,
  };
}

export function mergeMemory(base: CharacterMemory, patch: CharacterMemoryPatch): CharacterMemory {
  return normalizeMemory({ ...base, ...patch, characterId: base.characterId });
}

function maxDate(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a >= b ? a : b;
}

function toneBucket(tone: DecisionTone): 'commercial' | 'life' | 'outreach' | 'rest' {
  if (tone === 'commercial') return 'commercial';
  if (tone === 'outreach') return 'outreach';
  if (tone === 'rest') return 'rest';
  return 'life';
}

function buildWeeklyCounts(snapshot: OperationalSnapshot): WeeklyActivityCounts {
  const days = snapshot.recentDays;
  let photoDays = 0;
  let videoDays = 0;
  let talkingDays = 0;
  let voiceDays = 0;
  let restDays = 0;
  let commercialDays = 0;
  let lifeDays = 0;
  let outreachDays = 0;
  let activeDays = 0;

  for (const d of days) {
    const hasPhoto = d.photoSubtypes.length > 0 || d.selfieCount > 0;
    const hasVideo = d.hadShortVideo;
    const hasTalking = d.hadTalkingVideo;
    if (hasPhoto) photoDays++;
    if (hasVideo) videoDays++;
    if (hasTalking) talkingDays++;
    // voice：Lite DayRecord 无独立字段；口播日视作需要配音
    if (hasTalking) voiceDays++;

    const bucket = toneBucket(d.tone);
    if (bucket === 'rest') restDays++;
    else {
      activeDays++;
      if (bucket === 'commercial') commercialDays++;
      else if (bucket === 'outreach') outreachDays++;
      else lifeDays++;
    }
    // 有产出也算 active（即使 tone 标 rest）
    if ((hasPhoto || hasVideo || hasTalking) && bucket === 'rest') {
      // already counted rest; still note activity via fatigue inputs
    }
  }

  return {
    photoDays,
    videoDays,
    talkingDays,
    voiceDays,
    restDays,
    commercialDays,
    lifeDays,
    outreachDays,
    activeDays: Math.max(activeDays, commercialDays + lifeDays + outreachDays),
    consecutiveCommercialDays: snapshot.consecutiveCommercialDays,
    yesterdayHadTalking: snapshot.yesterdayHadTalking,
    weekSelfieCount: snapshot.weekSelfieCount,
  };
}

export interface GetCharacterMemoryInput {
  snapshot: OperationalSnapshot;
  todayIndex: number;
  asOfDate?: string;
}

/**
 * 唯一 Memory 入口：从 OperationalSnapshot 派生 CharacterMemory
 */
export function getCharacterMemory(input: GetCharacterMemoryInput): CharacterMemory {
  const asOfDate = input.asOfDate || input.snapshot.asOfDate || shanghaiDateKey();
  const base = createDefaultMemory(input.snapshot.characterId, asOfDate, input.todayIndex);
  const days = input.snapshot.recentDays;

  let last_photo_date: string | null = null;
  let last_video_date: string | null = null;
  let last_talking_date: string | null = null;
  let last_voice_date: string | null = null;
  let last_rest_day: string | null = null;
  let last_post_date: string | null = null;
  let photo_count = 0;
  let video_count = 0;
  let talking_count = 0;
  let voice_count = 0;
  let rest_count = 0;

  for (const d of days) {
    const hasPhoto = d.photoSubtypes.length > 0 || d.selfieCount > 0;
    if (hasPhoto) {
      photo_count += Math.max(1, d.selfieCount || d.photoSubtypes.length);
      last_photo_date = maxDate(last_photo_date, d.date);
      last_post_date = maxDate(last_post_date, d.date);
    }
    if (d.hadShortVideo) {
      video_count += 1;
      last_video_date = maxDate(last_video_date, d.date);
      last_post_date = maxDate(last_post_date, d.date);
    }
    if (d.hadTalkingVideo) {
      talking_count += 1;
      voice_count += 1;
      last_talking_date = maxDate(last_talking_date, d.date);
      last_voice_date = maxDate(last_voice_date, d.date);
      last_post_date = maxDate(last_post_date, d.date);
    }
    if (d.tone === 'rest') {
      rest_count += 1;
      last_rest_day = maxDate(last_rest_day, d.date);
    }
  }

  const counts = buildWeeklyCounts(input.snapshot);
  const balance = calculateWeeklyBalance(counts);

  return normalizeMemory({
    ...base,
    last_post_date,
    last_photo_date,
    last_video_date,
    last_voice_date,
    last_talking_date,
    last_rest_day,
    photo_count_this_week: photo_count,
    video_count_this_week: video_count,
    talking_count_this_week: talking_count,
    voice_count_this_week: voice_count,
    rest_count_this_week: rest_count,
    selfie_count_this_week: input.snapshot.weekSelfieCount,
    yesterday_had_selfie: input.snapshot.yesterdayHadSelfie,
    yesterday_had_talking: input.snapshot.yesterdayHadTalking,
    consecutive_commercial_days: input.snapshot.consecutiveCommercialDays,
    ...balance,
  });
}

/** 由 CharacterMemory 派生决策约束（唯一约束出口，非第二套 Memory） */
export interface CharacterMemoryConstraints {
  blockSelfie: boolean;
  blockTalkingVideo: boolean;
  forceLifeTone: boolean;
  forceRestDay: boolean;
  notes: string[];
}

export function constraintsFromCharacterMemory(memory: CharacterMemory): CharacterMemoryConstraints {
  const notes: string[] = [];
  let blockSelfie = false;
  let blockTalkingVideo = false;
  let forceLifeTone = false;
  let forceRestDay = false;

  if (memory.yesterday_had_selfie) {
    blockSelfie = true;
    notes.push('昨天已发自拍，今天避免自拍');
  }

  if (memory.yesterday_had_talking) {
    blockTalkingVideo = true;
    forceRestDay = true;
    notes.push('昨天已发口播，今天休息');
  }

  if (memory.consecutive_commercial_days >= 2 || memory.commercial_ratio >= 0.6) {
    forceLifeTone = true;
    notes.push(
      memory.commercial_ratio >= 0.6
        ? `本周商业占比偏高（${Math.round(memory.commercial_ratio * 100)}%），今天改发生活感`
        : '连续两天商业内容，今天改发生活感',
    );
  }

  if (memory.selfie_count_this_week >= 2) {
    blockSelfie = true;
    notes.push('本周已自拍两次，本周不再自拍');
  }

  if (memory.fatigue_score >= 0.7) {
    forceRestDay = true;
    notes.push(`疲劳偏高（${Math.round(memory.fatigue_score * 100)}），今天安排休息`);
  } else if (memory.fatigue_score >= 0.45 && memory.energy_level < 0.5) {
    blockTalkingVideo = true;
    notes.push('精力不足，今天暂缓口播');
  }

  if (memory.fatigue_score > 0) {
    notes.push(`fatigue_score=${memory.fatigue_score.toFixed(2)} energy=${memory.energy_level.toFixed(2)}`);
  }
  if (memory.commercial_ratio > 0 || memory.interaction_ratio > 0) {
    notes.push(
      `commercial_ratio=${memory.commercial_ratio.toFixed(2)} interaction_ratio=${memory.interaction_ratio.toFixed(2)} trust_ratio=${memory.trust_ratio.toFixed(2)}`,
    );
  }

  return { blockSelfie, blockTalkingVideo, forceLifeTone, forceRestDay, notes };
}
